import type { AmplifyUser } from "@aws-amplify/ui";
import type { SxProps } from "@mui/material";
import { Tooltip } from "@mui/material";
import type { TCurrencyData } from "components/AdminPanel/types";
import { getCurrencySymbolFromMap } from "config/currencySymbols";
import { getCountries } from "countries";
import { currenciesList } from "currenciesList";
import { differenceInCalendarDays, format, formatDistanceToNow, isDate, startOfToday } from "date-fns";
import { de, enUS, es, fr } from "date-fns/locale";
import i18n from "i18n";
import { t } from "i18next";
import type { TPeriod } from "store/periodStore";
import type { Currencies } from "store/settingsStore";
import { store } from "store/store";
import type { IBeneficiariesCompanies } from "store/types";
import type { IUser } from "types";

import vatRatesConfig from "../config/vatRates.json";
import { getLocale } from "./dates";
import { BodyHighlight } from "./TypographyComponents";
import type { TXMLCorrectRegexList } from "./xmlRegexList-helpers";

const CUSTOM_ROLE_PREFIX = "custom:";

/**
 * Check if a role value represents a custom role
 * @param roleValue The role value to check (e.g. "custom:1" or "owner")
 * @returns true if the role is a custom role
 */
export const isCustomRole = (roleValue: string | undefined | null): boolean => {
    return typeof roleValue === "string" && roleValue.startsWith(CUSTOM_ROLE_PREFIX);
};

/**
 * Extract the numeric ID from a custom role value
 * @param roleValue The custom role value (e.g. "custom:1")
 * @returns The role ID as a number, or null if not a valid custom role
 */
export const extractCustomRoleId = (roleValue: string | undefined | null): number | null => {
    if (!isCustomRole(roleValue)) return null;
    const idPart = roleValue!.slice(CUSTOM_ROLE_PREFIX.length);
    const id = parseInt(idPart, 10);
    return isNaN(id) ? null : id;
};

/**
 * Format a custom role ID into the "custom:{id}" format
 * @param roleId The role ID
 * @returns The formatted custom role string (e.g. "custom:1")
 */
export const formatCustomRoleId = (roleId: number): string => {
    return `${CUSTOM_ROLE_PREFIX}${roleId}`;
};

/** @deprecated Use getVatPercentage(countryCode) instead */
export const TITLE_VAT_PERCENTAGE = 1.055;

/**
 * Extract country code from a language code or return the country code as-is
 * @param code Language code (e.g., "fr-BE", "en-US") or country code (e.g., "BE", "US")
 * @returns Uppercase country code (e.g., "BE", "US")
 * @example
 * extractCountryCode("fr-BE") // "BE"
 * extractCountryCode("fr-be") // "BE"
 * extractCountryCode("BE") // "BE"
 * extractCountryCode("be") // "BE"
 */
export const extractCountryCode = (code: string): string => {
    if (!code) return "";
    const match = code.match(/(?:^|-)([a-zA-Z]{2})$/);
    const extracted = match ? match[1].toUpperCase() : "";
    // Backend uses 'UK' as the country code; ISO 3166-1 is 'GB'.
    return extracted === "UK" ? "GB" : extracted;
};

/**
 * Get VAT percentage factor for a given country code
 * @param countryCode ISO country code (e.g., "FR", "DE")
 * @returns VAT percentage factor (e.g., 1.055 for France), or 1 if country not found (no VAT applied)
 */
export const getVatPercentage = (countryCode?: string): number => {
    if (!countryCode) return 1;
    const code = countryCode.toUpperCase();
    return vatRatesConfig.vatRates[code] ?? 1;
};

/**
 * Get currency symbol from config map
 * @param currencyCode ISO currency code (e.g., "EUR", "USD", "GBP", "CAD")
 * @returns Currency symbol (e.g., "€", "$", "£")
 */
export const getCurrencySymbol = (currencyCode: string): string => {
    return getCurrencySymbolFromMap(currencyCode);
};

/**
 * Check if a country has a VAT rate configured
 * @param countryCode ISO country code (e.g., "FR", "DE") or language code (e.g., "fr-FR", "fr-BE")
 * @returns true if the country has a VAT rate in the config
 */
export const hasVatRate = (countryCode?: string): boolean => {
    if (!countryCode) return false;
    const normalizedCode = extractCountryCode(countryCode);
    if (!normalizedCode) return false;
    return normalizedCode in vatRatesConfig.vatRates;
};

/**
 * Apply VAT to a price for display (multiply by VAT factor)
 * @param price The price in cents (HT)
 * @param countryCode ISO country code for VAT rate lookup (returns unchanged price if country not found)
 * @returns Price with VAT applied, or unchanged price if country not matched
 */
export const applyVatToPrice = (price: number, countryCode: string): number => {
    const normalizedCountryCode = extractCountryCode(countryCode ?? "");
    return price * getVatPercentage(normalizedCountryCode);
};

/**
 * Remove VAT from a price (divide by VAT factor)
 * @param price The price with VAT (TTC)
 * @param countryCode ISO country code for VAT rate lookup (returns unchanged price if country not found)
 * @returns Price without VAT (HT), or unchanged price if country not matched
 */
export const removeVatFromPrice = (price: number, countryCode: string): number => {
    const normalizedCountryCode = extractCountryCode(countryCode ?? "");
    return price / getVatPercentage(normalizedCountryCode);
};

/**
 * Convert a given date object (`string` or `Date`) to a formatted date
 * @example
 * ```js
 * formatLocaleDateString(date, false) => "January 01, 2022"
 * formatLocaleDateString(date, true) => "01/01/2022"
 * formatLocaleDateString(date, true, true) => "01/01/2022, 12:00 AM"
 * formatLocaleDateString(date, true, false, undefined) => "01/01/2022" at UTC
 * formatLocaleDateString(date, true, false, null) => "01/01/2022" default timezone (unspecified)
 * ```
 * @param date The date to convert to locale date string
 * @param numeric The returned date format, ie: Jan 1, 2022 or 01/01/2022
 * @param showTime Boolean to show or not the time, ie: 01/01/2022, 12:00 AM
 * @returns The formatted date
 */
export const formatLocaleDateString = ({
    date,
    options = { numeric: false, showTime: false, timeZone: "UTC" },
}: {
    date: Date | string;
    options?: {
        numeric?: boolean;
        showTime?: boolean;
        timeZone?: null | string; // null to use the default timezone
    };
}) => {
    const { showTime, timeZone, numeric } = options;

    const timeStrings: any = showTime ? { hour: "numeric", minute: "numeric" } : {};

    const dateInstance = typeof date === "string" ? new Date(date) : date;

    if (dateInstance instanceof Date && !isNaN(dateInstance.getTime())) {
        return dateInstance.toLocaleString(i18n.language, {
            timeZone: timeZone === null ? undefined : timeZone,
            ...(numeric
                ? { day: "numeric", month: "numeric", year: "numeric", ...timeStrings }
                : { day: "2-digit", month: "long", year: "numeric", ...timeStrings }),
        });
    }

    return "-";
};

export const processIfCustomAttributes = (name: string) => {
    const prefix = "custom:";
    return name.startsWith(prefix) ? name.slice(prefix.length) : name;
};

/**
 * Returns a formatted string to show a file size that will be either 429kb or 1.2mb
 * @example
 * ```js
 * getFileSize(429567) => 429kb
 * getFileSize(1245567) => 1.2mb
 * ```
 * @param size The size of the given file in bytes
 * @returns The formatted string
 */
export const getFileSize = (size: number) => {
    if (size < 800000) {
        return (size / 1000).toFixed(0) + (i18n.language === "en" ? "kb" : "ko");
    } else if (size > 800000) {
        return (size / 1000000).toFixed(1) + (i18n.language === "en" ? "mb" : "mo");
    }
};

//TODO: pass name and lastName as two different variables
export const getInitials = (authorName: string) => {
    const [name, lastName, ...rest] = authorName?.split(" ") ?? ["N", "A"];
    if (rest) {
        if (rest.length > 0) {
            return (name.slice(0, 1) + rest.at(-1)?.slice(0, 1)).toLocaleUpperCase();
        } else {
            return lastName && lastName.length > 0
                ? (name.slice(0, 1) + lastName.slice(0, 1)).toLocaleUpperCase()
                : name.slice(0, 1);
        }
    }
};

/**
 * Returns string of author's name with firstname, first letter of lastname and dot
 * ```js
 * nameTransform("Hello World") => "Hello W."
 * ```
 * @param author Author's name
 * @return Return the formatted string
 */
export const nameTransform = (name: string) => {
    if (!name?.includes(" ")) return name;
    const firstName = name.slice(0, name.indexOf(" "));
    const lastNameInitial = name.charAt(name.indexOf(" ") + 1).toUpperCase();

    return `${firstName} ${lastNameInitial}.`;
};

/**
 * Simple function to capitalize first letter of a word
 * @param name HELLO -> Hello
 */
export const capitalizeFirstLetter = (name: string) => {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

/**
 * Returns a formatted filename for statements
 * @param author Author's name
 * @param title Title's name
 * @param type Statement type (gross, ...)
 * @param date Today's date
 * @returns A formatted file name
 */
export const getStatementFilename = (author: string, title: string, type: string, date: Date) =>
    `${author ? author.trim().replaceAll(" ", "_") + "_" : ""}` +
    `${title ? title.trim().replaceAll(" ", "_") + "_" : ""}` +
    `${type.replaceAll(" ", "_")}_` +
    `${formatLocaleDateString({ date, options: { numeric: true, timeZone: "UTC" } }).replaceAll("/", "_")}` +
    ".pdf";

export const base64ImageEncode = async (file: File) => {
    if (file) {
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                resolve(reader.result);
            };
            reader.onerror = (error) => {
                reject(error);
            };
        });
    }
    return null;
};

/**
 * Returns a formatted string to show number with a better visualization. The locale depends on the react-i18n locale, changes from the settings page
 * @example
 * ```js
 * // with a french localization
 * formatNumber(2500) => 2 500
 * formatNumber(2500, {isCurrency: true}) => 2 500 €
 * formatNumber(2500, {isCurrency: true, always2Digit: true}) => 2 500,00 €
 * ```
 * @param number The number to format
 * @param options Set of options
 * @param isCurrency Returns a formatted number or a price (ex: `true => 25 €` | `false => 25`)
 * @param always2Digit Forces the 2 digits after the comma (ex: `true => 25.00 €` | `false => 25 €`)
 * @returns A formatted string
 */
export const formatNumber = (
    number: number,
    options: {
        isCurrency?: boolean;
        always2Digit?: boolean;
        noDigit?: boolean;
        preferredCurrency?: Currencies;
    } = { isCurrency: false, always2Digit: false, noDigit: true },
    overriden?: {
        minimumFractionDigits: number;
        maximumFractionDigits: number;
    },
) => {
    // Not the cleanest way to do this (reading state) but easiest way due to formatNumber being used everywhere
    const companyCurrency = store.getState()?.settings?.companySettings?.currency ?? "EUR";
    const selectedCurrency = options.preferredCurrency ?? companyCurrency;

    const currencyOptions = options.isCurrency
        ? {
              minimumFractionDigits: options.always2Digit ? 2 : 0,
              maximumFractionDigits: options.noDigit ? 0 : 2,
          }
        : overriden ?? ({} as any);

    // noDigit if negative value && noDigit true
    if (number < 0 && options.noDigit) {
        currencyOptions.minimumFractionDigits = 0;
        currencyOptions.maximumFractionDigits = 0;
    }

    const locale = i18n.language;
    const numericValue = isNaN(number) ? 0 : number ?? 0;
    const formatter = new Intl.NumberFormat(locale, { ...currencyOptions });

    if (!options.isCurrency) {
        return formatter.format(numericValue);
    }

    const customSymbol = getCurrencySymbol(selectedCurrency);
    if (selectedCurrency === "EUR") {
        return `${formatter.format(numericValue)}\u00A0${customSymbol}`;
    }
    // Prefix currencies: minus before symbol (-$2,000); format abs so Intl does not emit a leading "-"
    const digits = formatter.format(Math.abs(numericValue));
    return numericValue < 0 ? `-${customSymbol}\u00A0${digits}` : `${customSymbol}\u00A0${digits}`;
};

/**
 * Does the opposite of the function formatNumber()
 * Take as param a number string "2 500,50 €"
 * Returns 2 500
 * @param priceStr string price
 */
export const formatStrNbToNumber = (priceStr: string) => {
    const amount = priceStr
        .replace(/\s/g, "")
        .replace(/[^\d,.-]/g, "")
        .replace(",", ".");

    if (amount.length) {
        return +amount;
    }
    return priceStr;
};

/**
 * Takes number with comma and replaces it with dot, making sure it's always a correct number.
 * @param number The number typed into input.
 * @returns A number.
 */
export const replaceComma = (number: string | number) => {
    const cleanedValue = number.toString().replace(",", ".");
    return +cleanedValue;
};

/**
 * Returns a formatted string about date and time for export file
 * ```js
 * // Example
 * getDateForFileName() => "2023-02-11_15-34-05"
 * ```
 * @returns A formatted string of date & time
 */
export const getDateForFileName = (givenDate?: Date) => {
    const date = givenDate || new Date();
    return `${date.getFullYear()}-${date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1}-${
        date.getDate() < 10 ? "0" + date.getDate() : date.getDate()
    }_${date.getHours() < 10 ? "0" + date.getHours() : date.getHours()}-${
        date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes()
    }-${date.getSeconds() < 10 ? "0" + date.getSeconds() : date.getSeconds()}`;
};

/**
 * Returns the right word depending on time of the day
 * @example
 * ```js
 * // At 6 am
 * renderHello() => Hello | Bonjour
 * // At 6 pm
 * renderHello() => Good evening | Bonsoir
 * ```
 * @returns A i18n string that is either "Hello" (Bonjour) or "Good evening" (Bonsoir)
 */
export const renderHello = () => {
    const currentHour = new Date().getHours();
    return currentHour >= 6 && currentHour < 17 ? "LogIn.Hello" : "LogIn.GoodEvening";
};

/**
 * Normalizes a date then formats it as YYYY-MM-DD (ISO 8601 calendar date).
 */
export const formatDateToIso = (date: Date | string) => {
    return format(getCleanStartorEndDay(date), "yyyy-MM-dd");
};

/**
 * Returns a formatted filename for statements
 * @param author Author's name
 * @param title Title's name
 * @param type Statement type (gross, ...)
 * @param date Today's date
 * @returns A formatted file name
 */

export const formatAttributeName = (name: string) => {
    const capitalisedName = name.charAt(0).toUpperCase() + name.slice(1);
    if (name.includes("_")) {
        return capitalisedName.replaceAll("_", " ");
    } else {
        return (name = capitalisedName.split(/(?=[A-Z])/).join(" "));
    }
};

export const generateRandomIssn = () => {
    let result = "";
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    for (let i = 0; i <= 8; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
        if (i == 3) result += "-";
    }
    return result;
};

/**
 * Function to mask some informations in string.
 * Accept one string in arg, return a string with same length and some character are masked
 * @param original String to mask
 * @param countNotMasked Number of caractere not masked at the end of the string (default = 3)
 * @returns Masked String with same length if count are inferior, return original string otherwise
 */
export const maskString = (original: string = "", countNotMasked: number = 3) => {
    if (original && original.length > countNotMasked) {
        const maskedString = `${Array(original.length - countNotMasked)
            .fill("•")
            .join("")}${original.slice(-countNotMasked)}`;
        return maskedString;
    }
    return original;
};

/**
 * Validates a French NIR (Numéro d'Inscription au Répertoire) by verifying its control key.
 * France-specific: expects exactly 15 digits (including the 2-digit control key).
 * For URSSAF declaration use only — in edit forms, validate format only (not the key).
 * @param nir NIR to check (spaces are stripped automatically)
 */
export const calculateNIRControlKey = (nir: string) => {
    nir = nir.replace(/\s/g, "");
    if (nir.length !== 15) return false;
    const cleanedNir = nir.includes("2A") ? nir.replace("2A", "19") : nir.replace("2B", "18");
    const providedControlKey = +cleanedNir.substring(13, 15);
    const numbers = +cleanedNir.substring(0, 13);
    const rest = numbers % 97;
    const substraction = 97 - rest;

    return providedControlKey == substraction;
};

/** UK National Insurance format exposed by the API (`QQ 12 34 56 C`). */
export const socialSecurityFormatAcceptsLetters = (format: string | null): boolean =>
    !!format && (format.includes("[A-Da-d]") || format.includes("[A-D]"));

export const getSocialSecurityPlaceholder = (format: string | null): string => {
    if (!format) return ""; // No format constraint (e.g. Greece)
    if (format.includes("{13,15}") || format.includes("{15}")) return "0 00 00 000 000 00 00"; // France NIR
    if (format.includes("{11}")) return "00 00 00 000 00"; // Belgium NISS
    if (socialSecurityFormatAcceptsLetters(format)) return "QQ 12 34 56 C"; // UK National Insurance
    return "000 000 000"; // Canada: 9-digit
};

/**
 * Client hint for a social security value.
 * Letter formats (UK NI) are tested against the API regex after separators are removed.
 * Digit formats keep the previous length check.
 */
export const isSocialSecurityFormatMismatch = (format: string | null, value: string | undefined): boolean => {
    if (!format || !value) return false;
    const compact = value.replace(/[\s-]/g, "");
    if (!compact) return false;

    if (socialSecurityFormatAcceptsLetters(format)) {
        return !new RegExp(format).test(compact);
    }

    const rangeMatch = format.match(/\{(\d+),(\d+)\}/);
    const exactMatch = format.match(/\{(\d+)\}/);
    const minLength = rangeMatch ? parseInt(rangeMatch[1], 10) : exactMatch ? parseInt(exactMatch[1], 10) : 0;
    const maxLength = rangeMatch ? parseInt(rangeMatch[2], 10) : exactMatch ? parseInt(exactMatch[1], 10) : 0;
    if (minLength <= 0) return false;
    return compact.length < minLength || compact.length > maxLength;
};

/** Inline error for a social security field. UK uses the NI example; other countries keep the French NIR check. */
export const socialSecurityFieldError = (
    format: string | null,
    value: string | undefined,
    translate: (key: string, options?: Record<string, string>) => string,
): { message: string } | undefined => {
    if (socialSecurityFormatAcceptsLetters(format)) {
        return isSocialSecurityFormatMismatch(format, value)
            ? { message: translate("ServerError.Field.socialSecurityNumber") }
            : undefined;
    }
    if (value && value.length > 0 && !calculateNIRControlKey(value)) {
        return {
            message: translate("NewBeneficiary.FieldSeemInvalid", {
                field: translate("Beneficiary.socialSecurityNumber"),
            }),
        };
    }
    return undefined;
};

/**
 * Clean a text from XML strings with Regex
 * @param XMLRegexList List of XML Regex
 * @param text The text to clean (string)
 * @returns return the text cleaned
 */
export const correctXMLText = (XMLRegexList: TXMLCorrectRegexList, text: string) => {
    try {
        let cleanedText: string = text;
        XMLRegexList.regex.forEach((item: any, i: any) => {
            cleanedText = cleanedText.replace(item, XMLRegexList.speChar[i]);
        });

        return cleanedText;
    } catch (error) {
        return "";
    }
};

/**
 * Find the translation according to key sent, if key doesn't exist provide fallback.
 * @param key Translation key wanted
 * @param fallback Default translation if key doesn't exist
 */
export const translationsWithFallback = (key: unknown, fallback: string = "ServerError.InternalServerError") => {
    // Callers pass raw API error payloads; their `message` is not always a string
    // (objects, arrays), and i18next crashes on non-string keys.
    const safeKey = typeof key === "string" && key ? key : "ServerError.InternalServerError";
    return t(safeKey) === safeKey ? t(fallback) : t(safeKey);
};

/**
 * Maps an AWS Cognito / Amplify auth error code to a user-facing translation key.
 *
 * This is the single source of truth for "known" auth errors: a code listed here
 * is a routine user-input / account-state problem, so it both gets a friendly
 * message ({@link getCognitoErrorMessage}) and is treated as expected
 * ({@link isExpectedCognitoError}) — i.e. not worth reporting to Sentry.
 * Exception: NetworkError gets its message but is expected only while the browser is offline.
 * Anything not listed is unexpected: generic message + reported.
 */
export const COGNITO_ERROR_MESSAGE_KEYS: Record<string, string> = {
    NotAuthorizedException: "LogIn.Error",
    UserNotFoundException: "LogIn.Error",
    UserNotConfirmedException: "LogIn.UserNotConfirmedException",
    PasswordResetRequiredException: "LogIn.PasswordResetRequiredException",
    CodeMismatchException: "LogIn.CodeMismatchException",
    ExpiredCodeException: "LogIn.ExpiredCodeException",
    LimitExceededException: "LogIn.LimitExceededException",
    TooManyRequestsException: "LogIn.TooManyRequestsException",
    TooManyFailedAttemptsException: "LogIn.TooManyRequestsException",
    InvalidPasswordException: "LogIn.InvalidPasswordException",
    InvalidParameterException: "LogIn.InvalidParameterException",
    UsernameExistsException: "LogIn.UsernameExistsException",
    // Set by amazon-cognito-identity-js when the request never reached Cognito. Expected only while offline,
    // see isExpectedCognitoError.
    NetworkError: "LogIn.NetworkError",
};

/** Resolves an Amplify error to its Cognito code (falls back to the JS error name). */
const getCognitoErrorCode = (error: any): string | undefined => error?.code ?? error?.name;

/**
 * Returns a localized, user-facing message for an AWS Cognito / Amplify auth error.
 * @param error The error thrown by an `Auth.*` call (uses `error.code`, then `error.name`).
 */
export const getCognitoErrorMessage = (error: any): string => {
    const code = getCognitoErrorCode(error);
    const key = (code && COGNITO_ERROR_MESSAGE_KEYS[code]) || "LogIn.GenericError";
    return translationsWithFallback(key, "LogIn.GenericError");
};

/**
 * Whether an auth error is an expected, routine condition (known code with a
 * friendly message) rather than an unexpected failure worth sending to Sentry.
 */
export const isExpectedCognitoError = (error: any): boolean => {
    const code = getCognitoErrorCode(error);
    // The SDK labels every failed fetch NetworkError, CORS/CSP blocks and endpoint outages included:
    // only a browser that knows it is offline is routine, anything else must still reach Sentry.
    if (code === "NetworkError") return !navigator.onLine;
    return !!code && code in COGNITO_ERROR_MESSAGE_KEYS;
};

export const frenchDateRegex = /^(?:0[1-9]|[12][0-9]|3[01])\/(?:0[1-9]|1[0-2])\/(?:\d{4})$/;

/**
 * Turn number into alphabet like any spreadsheets
 * Snippet from https://stackoverflow.com/questions/36129721/convert-number-to-alphabet-letter
 * @param number
 * @param res string prefix
 * @returns string
 */
export const numberToLetter = (number: number, res: string = "") => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

    let charIndex = number % alphabet.length;
    let quotient = number / alphabet.length;

    if (charIndex - 1 == -1) {
        charIndex = alphabet.length;
        quotient--;
    }

    const result = alphabet.charAt(charIndex - 1) + res;

    if (quotient >= 1) {
        numberToLetter(+quotient, result);
    } else {
        return result;
    }
};

export const convertAnyAsArray = (value: any): any[] => {
    return value && value.toString().length ? (Array.isArray(value) ? value : [value]) : [];
};

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export const isObject = (item: any) => {
    return item && typeof item === "object" && !Array.isArray(item);
};

/**
 * Copied from some deep dark SO answer.
 * It does the job.
 */
/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 */
export const mergeDeep = (target: Record<string, any>, ...sources: any[]): Record<string, any> => {
    if (!sources.length) return target;
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                mergeDeep(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }

    return mergeDeep(target, ...sources);
};

/**
 * Clamp a value between a minimum and a maximum
 * @param value value to clamp
 * @param min minimum value
 * @param max maximum value
 */
export const clamp = (value: number, min: number, max: number) => {
    return Math.min(Math.max(value, min), max);
};

export const downloadApiExport = (exportResponse: { url: string }, fileName: string) => {
    downloadFile({ source: exportResponse.url, fileName, isLink: true });
};

/**
 * Function to download file either from File or a link (ex: "data:application.....")
 * @param source File or link.
 * @param fileName Name of file with extension included.
 * @param isLink whether source is link or not (default to false)
 */
export const downloadFile = ({
    source,
    fileName,
    isLink = false,
}: {
    source: any;
    fileName: string;
    isLink?: boolean;
}) => {
    const downloadLink = document.createElement("a");

    if (isLink) {
        downloadLink.href = source;
    } else {
        downloadLink.href = URL.createObjectURL(source);
    }
    downloadLink.download = fileName;
    downloadLink.click();
};

/**
 * Verify if a date is ISO format
 * @param {string} date
 * @returns {boolean}
 */
export const isIsoDate = (date: string) => {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(date)) return false;
    const d = new Date(date);
    return d instanceof Date && !isNaN(d.getTime()) && d.toISOString() === date; // valid date
};

/**
 * Find currency data in the list by it code -> 'EUR' -> { name: "Euro", code: "EUR", flag: flagUrl }
 * @param {string} currencyCode ex : JPY, EUR, USD
 */
export const getCurrencyData = (currencyCode: string) => {
    return currenciesList().find((currencyData) => currencyData.code == currencyCode.toUpperCase());
};

/**
 * Get currencies options for autocomplete
 * @param currenciesList List of currencies
 */
export const getCurrenciesOptions = (currenciesList: string[]) => {
    const options: TCurrencyData[] = [];
    currenciesList.forEach((currencyCode) => {
        const currencyData = getCurrencyData(currencyCode);
        options.push(currencyData);
    });
    return options;
};

/**
 * For MUI date picker component disable weekends
 * @param date
 */
export const disableWeekends = (date: Date) => {
    return date.getDay() === 0 || date.getDay() === 6;
};

export const isSetupPage = () => window.location.href.includes("/agreements/new");
// window.location.href.includes("titles") || window.location.href.includes("beneficiars");

export const isContractValidationPage = () => {
    return window.location.href.includes("/agreements/") && !window.location.href.includes("/agreements/new");
};

export const isContractSettingsPage = () => window.location.href.includes("/settings/contracts");

/**
 * Find avatar url for beneficiar company
 * @param companyId bp company ID
 * @param bpCompagnies
 */
export const getBPAvatarUrl = (companyId: number, bpCompagnies: IBeneficiariesCompanies[]) => {
    return (
        bpCompagnies.find((company: any) => company.id == companyId)?.avatarUrl ??
        "https://placehold.co/100x100/FFF/FFF"
    );
};

/**
 * Find company name for beneficiar company
 * @param companyId bp company ID
 * @param bpCompagnies
 */
export const getBPCompanyName = (companyId: number, bpCompagnies: IBeneficiariesCompanies[]) => {
    return bpCompagnies.find((company: any) => company.id == companyId)?.name ?? "-";
};

/**
 * Used in LastElementTable to determine whether to create new section based on date.
 * @param statement current statement data
 * @param index index of statement
 * @param statements all statements data
 * @param property which property containes date to compare
 */
export const isNewDate = (statement: any, index: number, statements: any, property: string) => {
    const previousItem = statements[index - 1] ?? {};
    return !(new Date(statement[property]).getUTCFullYear() == new Date(previousItem[property]).getUTCFullYear());
};

/**
 * Return a letter for a given index
 * 0 = A, 2 = C, 26 = AA ..
 */
export const indexToLetter = (index: number) => {
    return index < 0 ? "" : indexToLetter(index / 26 - 1) + String.fromCharCode(65 + (index % 26));
};

/**
 * Checks if a value is null or undefined
 * @param value
 * @returns boolean
 */
export const isNullOrUndefined = (value: any) => {
    return value === undefined || value === null;
};

export const sortAlphabetically = (value: any, key: string, order?: "ASC" | "DESC") => {
    const ASC = [...value].sort((a, b) => a[key].localeCompare(b[key]));
    const DESC = [...value].sort((a, b) => b[key].localeCompare(a[key]));
    return order ? (order === "ASC" ? ASC : DESC) : ASC;
};

/**
 * Sorts array of object by property passed.
 * @param array Array to be sorted.
 * @param key Property with which to sort. Nested properties allowed (ex: "data.quantities")
 * @param order Order of the sort. By default, DESC.
 * @returns Sorted array.
 */
export const sortByProperty = (array: any, key: string, order = "DESC") => {
    let ASC = array;
    let DESC = array;

    // This handles nested keys so it is possible to pass a key like "data.quantities"
    const resolveKey = (obj, key) => {
        return key.split(".").reduce((acc, part) => acc && acc[part], obj);
    };

    if (key.toLowerCase().endsWith("date") || key.toLowerCase().endsWith("at")) {
        if (order === "ASC") {
            ASC = [...array].sort(
                (a, b) => new Date(resolveKey(a, key)).getTime() - new Date(resolveKey(b, key)).getTime(),
            );
        } else {
            DESC = [...array].sort(
                (a, b) => new Date(resolveKey(b, key)).getTime() - new Date(resolveKey(a, key)).getTime(),
            );
        }
    } else {
        if (order === "ASC") {
            ASC = [...array].sort((a, b) => {
                return resolveKey(a, key) - resolveKey(b, key);
            });
        } else {
            DESC = [...array].sort((a, b) => resolveKey(b, key) - resolveKey(a, key));
        }
    }

    return order ? (order === "ASC" ? ASC : DESC) : ASC;
};

/**
 * Returns array ordered with true values first.
 * @param array Array to be ordered.
 * @param property The property that is a boolean.
 */
export const sortArrayByBooleanProperty = (array: any, property: string) => {
    return array.sort((a, b) => {
        if (a[property] && !b[property]) {
            return -1;
        } else if (!a[property] && b[property]) {
            return 1;
        } else {
            return 0;
        }
    });
};

export const isObjectEmpty = (object: any) => {
    if (!object) return true;
    return JSON.stringify(object) === "{}";
};

/**
 * Get country name by its code
 * @param countryCode ex FR
 * @returns FRANCE
 */
export const getCountryName = (countryCode: string) => {
    return getCountries().find((country) => country.code === countryCode)?.name ?? countryCode;
};

/**
 * Get country code by its name
 * @param countryName name of a country ex France
 * @returns FR
 */
export const getCountryCode = (countryName: string) => {
    return getCountries().find((country) => country.name === countryName)?.code ?? countryName;
};

/**
 * Either return classic user attribute
 * or return SAML-authenticated user attribute
 * @param attribute
 * @param user
 * @returns
 */
export const getCognitoUserAttribute = (attribute: string, user: AmplifyUser) => {
    if (user?.attributes) {
        return user?.attributes[attribute];
    }

    return user?.getSignInUserSession()?.getIdToken()?.payload[attribute];
};

/**
 * Returns a parsed JSON object if given a string, or the object itself if already parsed.
 * @param value A JSON object or a stringified JSON.
 * @returns JSON object
 */
export const getCleanJSON = (value) => {
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return typeof parsed === "object" && parsed !== null ? parsed : value;
        } catch {
            return value; // Return original string if it can't be parsed
        }
    }

    return value; // Already a parsed object
};

/**
 * If string can be parsed, returns JSON if not returns false.
 * @param value String to be parsed.
 * @returns JSON or false
 */
export const getJsonOrFalse = (value: string) => {
    try {
        const parsedValue = JSON.parse(value);
        if (typeof parsedValue === "object") {
            return parsedValue;
        }
    } catch (error: any) {
        return false;
    }
};

/**
 * Formats name of file to replace all spaces/dots with underscore.
 * @param fileName (string) Name of file uploaded
 * @returns (string)
 */
export const formatFileName = (fileName: string) => {
    const splitName = fileName.split(".");
    return `${splitName[0].replace(/[\s.]/g, "_")}.${splitName.pop()}`;
};

export const removeAccents = (value: string) => {
    return value?.normalize("NFD")?.replace(/[\u0300-\u036f]/g, "");
};

/**
 * Normalize the filename to decompose special characters
 */
export const normaliseFilename = (filename: string) => {
    return filename
        .normalize("NFD") // Decomposes characters like é to e and ́ (combining mark)
        .replace(/[\u0300-\u036f]/g, "") // Removes diacritical marks
        .replace(/[^a-zA-Z0-9_.-]/g, "_"); // Replaces any remaining special characters with underscores
};

/**
 * Check if a translation exist for a value
 * @param value (string) i18 literal string
 */
export const hasI18N = (value: string) => {
    return t(value) !== value;
};

/**
 * Reusable for forms prevent submit function on press keyboard Enter tab
 */
export const preventSubmit = (keyEvent: any) => {
    // stop submit on press key "enter", but enable it on the "summary" text area field
    if (keyEvent.target.className !== "form-text-area") {
        if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
            keyEvent.preventDefault();
        }
    }
};

export const removeAllSpaces = (value: string) => {
    return value?.replace(/[\s\u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g, "") ?? "";
};

/**
 * @param text - Text that contains brackets to remove, exemple {{name}} -> name
 */
export const removeBrackets = (text: string) => {
    return text.replace("{{", "").replace("}}", "");
};

/**
 * Formats the percentage to a number.
 *
 * @example
 * normalizePercentage("50%") // 50
 * normalizePercentage(0.5) // 50
 * normalizePercentage("0.5") // 50
 * normalizePercentage(50) // 50
 * normalizePercentage("123") // Error
 * normalizePercentage("123%") // Error
 * normalizePercentage(-1) // Error
 * normalizePercentage("1OO") // Error
 * normalizePercentage("0%") // 0
 *
 * @param value {string | number} - The value to format.
 *
 * @returns {number} - The formatted value.
 */
export const normalizePercentage = (value: string | number): number => {
    if (typeof value === "number") {
        if (value === 0) {
            return 0;
        }

        if (value < 0 || value > 100 || Number.isNaN(value)) {
            throw new Error("Invalid percentage value");
        }

        return value * (value <= 1 ? 100 : 1) * 100;
    } else if (typeof value === "string") {
        // This regex is __almost__ perfect. It will accept 100.xx but it'll be caught
        const numberRegex = /^(?:0|100|[1-9]\d?)(?:[.,]\d{1,2})?(?:%?)$/;
        const cleanedValue = value.trim().replace(/\s+/g, "").replace(",", ".");

        if (!numberRegex.test(cleanedValue)) {
            throw new Error("Invalid percentage value");
        }

        return normalizePercentage(parseFloat(cleanedValue));
    }
    throw new Error("Invalid type for distributorInvoicingPercentage");
};

/**
 * Get length of cumulated arrays in obj
 * Exemple usage :
 * const obj = {
 *  1: [ "cat", "dog" ],
 *  2: [ "chicken" ]
 * }
 * getObjectArraysLength(obj) = 3;
 * @param obj Object with string as key and any data array as value
 */
export const getObjectArraysLength = (obj: Record<string, any[]>) => {
    const values: string[] = [];
    for (const key in obj) {
        obj[key].forEach((item) => values.push(item));
    }
    return values.length;
};

/**
 * When fetching data, check if the request is the final loaded
 * By comparing stored request Id and current request Id
 * It can be used for filters searchQuery request
 * @param { string } storedReqId last stored req Id
 * @param { string } currentReqId current request Id
 */
export const isFinalReq = (storedReqId: string, currentReqId: string) => {
    return storedReqId === currentReqId;
};

/**
 * Check if a value is Infinity and return boolean
 * @param { Infinity | "Infinity"} value
 */
export const isInfinity = (value: string | number) => {
    return value.toString() === "Infinity";
};

/**
 * Format vat price of sending form req
 * @param price
 * @param usesVAT uses vat company settings
 * @param currencyCode currency code
 * @param countryCode ISO country code for VAT rate lookup (defaults to FR)
 */
export const priceTransform = ({
    price,
    usesVAT,
    countryCode,
}: {
    price: string;
    usesVAT: boolean;
    countryCode?: string;
}) => {
    if (!price) price = "0";
    if (usesVAT) {
        const normalizedCountryCode = countryCode ? extractCountryCode(countryCode) : undefined;

        return Math.round((+price.toString().replace(",", ".") / getVatPercentage(normalizedCountryCode)) * 100);
    } else {
        return Math.round(+price.toString().replace(",", ".") * 100);
    }
};

/**
 * Format user list for display. Sorted by `created` descending.
 * Crealo admin (`useAuth` `isAdminCrealo`): users with an email (including @crealo.app).
 * Everyone else: exclude @crealo.app and rows without an email.
 * @param users userList
 * @param isAdminCrealo From `useAuth()`.
 */
export const normalizeUsersData = (users: IUser[], isAdminCrealo?: boolean) => {
    const byCreatedDesc = (a: IUser, b: IUser) => (b.created > a.created ? 1 : -1);
    const hasEmail = (u: IUser) => Boolean(u.email);
    const isCrealoUser = (u: IUser) => (u.email ?? "").endsWith("@crealo.app");

    if (isAdminCrealo) {
        return users.filter(hasEmail).sort(byCreatedDesc);
    }

    return users.filter((u) => !isCrealoUser(u) && hasEmail(u)).sort(byCreatedDesc);
};

/**
 * Returns date formatted with locale.
 * @param date
 * @param formatOption Default -> "19 Aug 2025" / month -> Aug 2025
 */
export const dateConvert = (date: Date, formatOption?: "" | "month") => {
    let formatting = "";
    switch (formatOption) {
        case "month":
            formatting = "MMM yyyy";
            break;
        default:
            formatting = "PP";
    }
    // Rebuild the Date from UTC calendar parts so date-fns formats the day the
    // backend stored (UTC midnight), not the local shift (off-by-one in Americas).
    const source = new Date(date);
    const utcCalendarDate = new Date(source.getUTCFullYear(), source.getUTCMonth(), source.getUTCDate());
    return format(utcCalendarDate, formatting, { locale: getLocale() });
};

/**
 * Format period name.
 * Default -> 01 Jan 2024 - 31 Dec 2024 (depending on locale).
 * @params format (optional)
 *  if numeric -> 01/01/2024 - 31/12/2024
 *  if month -> Jan 2024 - Dec 2024
 */
export const getPeriodName = ({ period, format = "" }: { period: TPeriod; format?: "numeric" | "month" | "" }) => {
    if (!period) return "";

    switch (format) {
        case "month":
            return `${dateConvert(period?.periodStart, "month")} - ${dateConvert(period?.periodEnd, "month")}`;
        case "numeric":
            return `${formatLocaleDateString({
                date: period.periodStart,
                options: { numeric: true, timeZone: "UTC" },
            })} - ${formatLocaleDateString({ date: period.periodEnd, options: { numeric: true, timeZone: "UTC" } })}`;
        default:
            return `${dateConvert(period?.periodStart)} - ${dateConvert(period?.periodEnd)}`;
    }
};

/** Same format as the term form period type selector (e.g. "6 mois - 01/01/2024 ↔︎ 31/12/2024"). */
export const getPeriodRedditionLabel = ({ period, monthsLabel }: { period: TPeriod; monthsLabel: string }) => {
    if (!period) return "";

    if (period.periodDuration) {
        const opts = { timeZone: "UTC" } as const;
        return `${period.periodDuration} ${monthsLabel} - ${formatLocaleDateString({
            date: period.periodStart,
            options: opts,
        })} ↔︎ ${formatLocaleDateString({ date: period.periodEnd, options: opts })}`;
    }

    return getPeriodName({ period });
};

/**
 * Ensures PureSx<T> contains only object-based styles
 */
type PureSx<T extends object> = Exclude<SxProps<T>, ReadonlyArray<unknown>>;
/**
 * Array containing only non-array styles
 */
type SxAsArray<T extends object> = Array<PureSx<T>>;

/**
 * Merges multiple sx style objects, arrays, or possibly falsey values (false or undefined), into a single style array.
 * It processes each style and flattens any arrays, returning a unified result.
 * Warning: considering that higher index of the array has higher specificity, the order of arguments passed to mergeSx should have impact --> to be confirmed when implementing.
 */
export const mergeSx = <T extends object>(...styles: (SxProps<T> | false | undefined)[]): SxProps<T> => {
    const capacitor: SxAsArray<T> = [];
    for (const sx of styles) {
        if (sx) {
            if (Array.isArray(sx)) {
                for (const sub of sx as SxAsArray<T>) {
                    capacitor.push(sub);
                }
            } else {
                capacitor.push(sx as PureSx<T>);
            }
        }
    }
    return capacitor;
};

// Example for mergeSx:
// const style1 = { color: 'red' };
// const style2 = false;
// const style3 = [{ margin: '16px' }, { padding: '8px' }];
// const style4 = { backgroundColor: 'blue' };

// const mergedStyles = mergeSx(style1, style2, style3, style4)
// --> returns:
// [
//     { color: 'red' },
//     { margin: '16px' },
//     { padding: '8px' },
//     { backgroundColor: 'blue' }
// ]

/**
 * Check if a value is a positive number
 * @param str string to test
 * 123-123 -> false
 * -2000 -> false
 * 1287*23 -> false
 * 122777382 -> true
 */
export const isPositiveInteger = (str) => {
    return /^\d+$/.test(str);
};

/**
 * Checks if a string is longer than maxLength allowed. If so slices string and adds ellipsis at end.
 * @param text String to validate.
 * @param maxLength Max length of characters allowed.
 * @returns Truncated string with ellipsis or full string.
 */
export const truncateString = (text: string, maxLength: number) => {
    if (text.length > maxLength) {
        return text.slice(0, maxLength - 1) + "...";
    } else {
        return text;
    }
};

/**
 * Formats address depending on components passed:
 * > formatAddress("23 rue Crealo", "Paris", "75011", "France")
 *  Output: 23 rue Crealo - 75011 Paris, France"
 * > formatAddress("", "Paris", "75011", "France")
 * Output: 75015 Paris, France
 * formatAddress("23 rue Crealo", "", "", "France")
 * Output: 23 rue Crealo, France
 */
export const formatAddress = ({
    street,
    city,
    zipCode,
    country,
}: {
    street: string;
    city: string;
    zipCode: string;
    country: string;
}) => {
    const addressParts: string[] = [];

    if (street) addressParts.push(street);
    if (zipCode) addressParts.push(zipCode);
    if (city) addressParts.push(city);
    if (country) addressParts.push(country);

    // Build the address format based on presence of components
    let formattedAddress = "";
    if (street) formattedAddress += street;

    if (zipCode || city) {
        if (street) formattedAddress += " - ";
        if (zipCode) formattedAddress += zipCode;
        if (city) formattedAddress += " " + city;
    }

    if (country) {
        if (zipCode || city) formattedAddress += ", ";
        formattedAddress += country;
    }

    return formattedAddress.trim() === "" ? "-" : formattedAddress;
};

/**
 * Returns key of a key/value pair.
 */
export const getKeyFromPair = (pair: object) => {
    const [key] = Object.entries(pair)[0]; // Extracts the first key
    return key;
};

/**
 * Transforms object of objects into an array of objects:
 * Example: {1898: {key: value, key value}, 45654: {key: value, key: value}}
 * => [{1898: {key: value, key value}}, {45654: {key: value, key: value}}]
 */
export const getArrayFromObject = (object: object) => {
    return Object.entries(object).map(([key, value]) => ({ [key]: value }));
};

/**
 *  This function groups an array of objects by a specified key,
 *  Returns an object where each key has for value an array of objects based on the same key value.
 *  @param {string} key - The key on which to group the objects
 *  @param {object[]} data - The array of objects to group
 *  @return {object} An object grouped on the key passed as param
 *
 *  @example
 *  const data = [
 *      { id: 1, kind: "cat", name: "Nao"},
 *      { id: 2, kind: "cat", name: "John"},
 *      { id: 3, kind: "parrot", name: "Doe" }
 *  ]
 * const groupedData = groupObjByKey("kind", data)
 * // Result:
 * // {
 * //   cat: [
 * //     { id: 1, kind: "cat", name: "Nao"},
 * //     { id: 2, kind: "cat", name: "John"}
 * //   ],
 * //   parrot: [
 * //     { id: 3, kind: "parrot", name: "Doe" }
 * //   ]
 * // }
 */
export const groupObjByKey = (key: string, data: object[]) => {
    return data.reduce((acc, item) => {
        const keyValue = item[key];
        if (!acc[keyValue]) acc[keyValue] = [];
        acc[keyValue].push(item);
        return acc;
    }, {});
};

/**
 * Check if a value is a number
 */
export const isNumber = (value) => typeof value === "number" && !isNaN(value);

/**
 * This function computes the evolution index between two values
 * @param {unknown} initialValue initial value
 * @param {unknown} finalValue final value
 * @returns Index evolution as number or null if one of params values isn't a number
 */
export const computeGrowthIndex = (initialValue: any, finalValue: any) => {
    if (!isNumber(initialValue) || !isNumber(finalValue) || initialValue === 0) {
        return null; // Invalid input
    }
    if (finalValue === 0) return -100;
    const index = ((finalValue - initialValue) / Math.abs(initialValue)) * 100;
    const roundedIndex = Math.ceil(index);
    return roundedIndex;
};

/**
 * Get current period based on activePeriod (id) saved in localStorage.
 */
export const getCurrentPeriod = (allPeriods: TPeriod[]): TPeriod | undefined => {
    const localActivePeriod = localStorage.getItem("activePeriod");
    if (!localActivePeriod) return;
    return allPeriods.find((item) => item.id === +localActivePeriod);
};

/**
 * Check if a passed date is a valid js date
 * @param {any} date any date value to check
 * @returns boolean
 */
export const isValidDate = (date: any) => isDate(date) && !isNaN(date.getTime());

/**
 * Returns date set X years after date passed (or current day if no date passed).
 */
export const incrementDateYears = ({
    yearDistance,
    referenceDate = new Date(),
}: {
    yearDistance: number;
    referenceDate: Date;
}) => {
    const date = referenceDate;
    const year = date.getFullYear();
    date.setFullYear(year + yearDistance);
    return date;
};

/**
 * Checks if the difference between the current date and the given date is within the specified number of days.
 * @param {string | Date | undefined | null} date - The target date, either as a Date object or an ISO string.
 * @param {number} days - The maximum number of days to check.
 * @returns {boolean} - True if the difference is within the specified days, false otherwise.
 *
 * Example for 1 month:
 * @example isDateWithinDays(date, 30) -> returns true or false
 */
export const isDateWithinDays = (date: string | Date | undefined | null, days: number): boolean => {
    if (!date) return false;

    const targetDate = typeof date === "string" ? new Date(date) : date;
    const today = startOfToday();

    const differenceInDays = differenceInCalendarDays(targetDate, today);
    return differenceInDays >= 0 && differenceInDays <= days;
};

/**
 * Creates new object only with keys wanted.
 * So takes a larger object and returns smaller one.
 * @param obj Object to dissect.
 * @param wantedKeys Keys we want to keep.
 */
export const getSubObject = <T extends object, K extends keyof T>(obj: T, wantedKeys: K[]): Pick<T, K> => {
    const newObject = {} as Pick<T, K>;
    for (const key of wantedKeys) {
        newObject[key] = obj[key];
    }

    return newObject;
};

/**
 * Converts a price to cents (integer)
 * @param price - The price to convert (can be number, string with dots or commas)
 * @param defaultValue - Value to return in case of error (default: 0)
 * @returns The price in cents as an integer
 * @example
 * toIntegerPrice(12.5) => 1250
 * toIntegerPrice("12,50") => 1250
 * toIntegerPrice("invalid") => 0
 * toIntegerPrice(null, 500) => 500
 */
export const toIntegerPrice = (price: string | number | null | undefined, defaultValue = 0): number => {
    try {
        // Handle numbers directly
        if (typeof price === "number") {
            return Math.round(price * 100);
        }

        // Handle null, undefined, empty string
        if (!price) {
            return defaultValue;
        }

        // Replace commas with dots and parse
        const sanitizedPrice = price.replace(/,/g, ".");
        const parsedPrice = parseFloat(sanitizedPrice);

        return isNaN(parsedPrice) ? defaultValue : Math.round(parsedPrice * 100);
    } catch {
        return defaultValue;
    }
};

/**
 * Check whether string date matches both formats we currently accept (YYYY-MM-DD or DD/MM/YYYY)
 * @param type If not specified, it checks for both types.
 * @returns boolean
 */
export const isDateFormatValid = ({ date, type }: { date: string; type?: "french" | "english" | undefined }) => {
    if (!date) return false;
    const cleanedDate = date.trim().replace(/\s+/g, "");

    if (type === "french") {
        return ddmmyyyyDateRegex.test(cleanedDate);
    } else if (type === "english") {
        yyyymmddDateRegex.test(cleanedDate);
    } else {
        return yyyymmddDateRegex.test(cleanedDate) || ddmmyyyyDateRegex.test(cleanedDate);
    }
};

/**
 * Takes a date string and returns corresponding Date (start or end of day)
 * disregarding any timezone logic.
 * @param input the date no matter the type
 * @param options for now, only one, time = "start" | "end". Time defauls to start if not specified.
 */
export const getCleanStartorEndDay = (input, options = { time: "start" }): Date => {
    let year, month, day;
    const endOfDay = options.time === "end";

    if (input instanceof Date) {
        // Handle native Date object
        year = input.getFullYear();
        month = input.getMonth(); // Already 0-indexed
        day = input.getDate();
    } else if (typeof input === "string") {
        // Strip the 'Z' if present — treat input as local time.
        const sanitized = input.replace(/Z$/, "");

        let match;

        // Match DD/MM/YYYY
        match = sanitized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (match) {
            const [_, dayStr, monthStr, yearStr] = match;
            day = parseInt(dayStr, 10);
            month = parseInt(monthStr, 10) - 1;
            year = parseInt(yearStr, 10);
        } else {
            // Match YYYY-MM-DD (or part of it)
            match = sanitized.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                const [_, yearStr, monthStr, dayStr] = match;
                year = parseInt(yearStr, 10);
                month = parseInt(monthStr, 10) - 1;
                day = parseInt(dayStr, 10);
            } else {
                throw new Error("Input must be a string of type DD/MM/YYYY or YYYY-MM-DD.");
            }
        }
    } else {
        throw new Error("Input must be a string or Date object.");
    }

    const hour = endOfDay ? 23 : 0;
    const minute = endOfDay ? 59 : 0;
    const second = endOfDay ? 59 : 0;
    const millisecond = endOfDay ? 999 : 0;

    return new Date(year, month, day, hour, minute, second, millisecond);
};

// Validate date format DD/MM/YYYY
export const ddmmyyyyDateRegex = /^(?:0[1-9]|[12][0-9]|3[01])\/(?:0[1-9]|1[0-2])\/(?:\d{4})$/;
/**
 * Regex for date format: YYYY-MM-DD
 */
export const yyyymmddDateRegex = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Checks if periodId corresponds to a closed period.
 */
export const isPeriodClosed = (currentPeriodId: number | undefined | null, periods: TPeriod[]): boolean => {
    if (!currentPeriodId) return false;
    const linkedPeriod = periods.find((item) => item.id === currentPeriodId);
    return linkedPeriod ? linkedPeriod?.isActive === 0 : false;
};

/**
 * Validate date format DD/MM/YYYY
 * @param dateString The string to validate
 * @returns boolean
 */
export const isValidDDMMYYYYDate = (dateString: string): boolean => {
    const cleanedDate = dateString.trim().replace(/\s+/g, "");
    return ddmmyyyyDateRegex.test(cleanedDate);
};

type Primitive = string | number | boolean | null | undefined;
export type InputType = Primitive | InputObject | InputArray;
interface InputObject {
    [key: string]: InputType;
}
interface InputArray extends Array<InputType> {}

/**
 * Formats payload of logs so it is easily readable.
 */
export const renderLogPayloadData = (input: InputType, parentKey = ""): JSX.Element[] => {
    if (typeof input === "string" || typeof input === "number" || typeof input === "boolean" || input === null) {
        return [
            <div key={parentKey} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <BodyHighlight style={{ fontSize: "12px" }}>{parentKey}</BodyHighlight>
                <Tooltip title={String(input)}>
                    <div
                        style={{
                            fontSize: "12px",
                            maxWidth: "200px",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                        }}
                    >
                        : {String(input) || "-"}
                    </div>
                </Tooltip>
            </div>,
        ];
    }

    if (Array.isArray(input)) {
        return input.flatMap((item, index) => renderLogPayloadData(item, `${parentKey}[${index}]`));
    }

    if (typeof input === "object") {
        return Object.entries(input).flatMap(([key, value]) =>
            renderLogPayloadData(value, parentKey ? `${parentKey}.${key}` : key),
        );
    }

    return [];
};

/**
 * Function to use within JS sort function. Allows sorting array of objects based on several properties.
 * @param {[string, 'asc' | 'desc'][]} keysWithOrder - Array of [key, order] tuples. Order of keys must be order of priority.
 * Ex:  multiSortWithOrder([['termId', 'asc'],['advancePaymentId', 'desc'],['sliceRowId', 'asc']])
 */
export const multiSortWithOrder = (keysWithOrder: [string, "asc" | "desc"][]) => {
    return (a, b) => {
        for (const [key, order] of keysWithOrder) {
            const valA = a[key];
            const valB = b[key];
            const direction = order === "desc" ? -1 : 1;

            if (valA < valB) return -1 * direction;
            if (valA > valB) return 1 * direction;
        }
        return 0;
    };
};

/**
 * Function to create a enum-simile from an object keys.
 * Specifically useful in forms to make sure the controlName matches the form's keys.
 */
export const createEnumFromObject = <T extends Record<string, unknown>>(obj: T) => {
    const enumObject = {};
    Object.keys(obj).forEach((key) => {
        enumObject[key] = key;
    });
    return enumObject as Record<keyof T, string>;
};

const dateFnsLocales: Record<string, Locale> = { fr, en: enUS, es, de };

export const timeAgo = (date: Date | string | null | undefined): string | null => {
    if (!date) return null;
    const locale = dateFnsLocales[i18n.language] || fr;
    return formatDistanceToNow(new Date(date), { addSuffix: true, locale });
};
